import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { styled, useMediaQuery, useTheme } from '@mui/material';
import { DesignTokens, GivelifyButton, GivelifyLabel } from '@givelify/ui';
import { webConfig } from 'webConfig';
import Logo from '../../components/Logo';

const Wrapper = styled('div')(() => ({
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
}));

const GivelifyLogo = styled(Logo)(() => ({
    marginBottom: 24,
}));

const ErrorPage: React.FC<{ code?: number }> = ({ code }) => {
    const { t } = useTranslation();
    const TRANSLATION_KEY = 'signup.acceptInvite.error';
    const scopedTranslate = useCallback(
        (key: string) => t(`${TRANSLATION_KEY}.${key}`),
        [t],
    );

    const copy = useMemo(
        () => ({
            400: scopedTranslate('400'),
            410: scopedTranslate('410'),
            404: scopedTranslate('404'),
            422: scopedTranslate('422'),
            common: scopedTranslate('common'),
            tryAgain1: scopedTranslate('tryAgain1'),
            tryAgain2: scopedTranslate('tryAgain2'),
            tryAgain3: scopedTranslate('tryAgain3'),
            continueToSignIn: scopedTranslate('continueToSignIn'),
            contactUs: scopedTranslate('contactUs'),
        }),
        [scopedTranslate],
    );

    const errorMessage = useMemo(
        () =>
            (code === 400 && copy['400']) ||
            (code === 410 && copy['410']) ||
            (code === 404 && copy['404']) ||
            (code === 422 && copy['422']) ||
            copy.common,
        [code, copy],
    );

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('mobile'));

    return (
        <Wrapper data-testid={`error${code}`}>
            <GivelifyLogo />
            <GivelifyLabel
                variant={isMobile ? 'body2B' : 'body1B'}
                text={errorMessage}
                style={{ marginBottom: 24 }}
                color={DesignTokens.color.textSecondary}
            />
            <GivelifyButton
                variant="primary"
                text={code === 400 ? copy.continueToSignIn : copy.contactUs}
                href={
                    code === 400
                        ? webConfig.givelifySignInPageUrl
                        : webConfig.givelifySupportUrl
                }
            />
        </Wrapper>
    );
};

export default ErrorPage;
